import { useEffect, useRef, useState } from 'react'
import Select, { components, GroupBase, Props as ReactSelectProps } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { getColor } from '../../../lib/tailwindTheme'
import { zIndex } from '../../../lib/zIndex'
import { FORM_FIELD_HEIGHT_PX } from '../../../lib/formStyles'

const FIELD_HEIGHT = `${FORM_FIELD_HEIGHT_PX}px`

export type SearchableSelectOption<V = string> = {
	value: V
	label: string
	[key: string]: unknown
}

// GEOMETRY NOTES — read before touching width/truncation logic below:
// 1. Control width is calculated up front from canvas-measured label widths + a fixed chrome
//    reserve (indicator/padding/border px), then applied as an exact CSS width — there is no
//    layout-driven auto-sizing, so any under-measurement directly clips text via the native
//    text-overflow: ellipsis on `singleValue` (silently, with no visual difference from a
//    correctly-sized clip until you actually count missing characters).
// 2. The component deliberately sets fontSize/fontFamily to 'inherit' everywhere (control,
//    valueContainer, input, singleValue) so it adapts to whatever ambient font context it's
//    rendered in (e.g. a heading zone at the page's default size vs. a text-sm table filter).
//    Measuring with a hardcoded font size is therefore wrong by a *proportional* amount on any
//    page whose ambient font differs from the assumption — this is a much bigger and easier to
//    miss error than the padding/border chrome constants, since it silently varies per consumer
//    rather than failing the same way everywhere. This is why `font` is threaded through
//    calculateWidths/truncateMiddlePreservingTail instead of being a fixed constant — always
//    read the real computed font from the DOM (see `measuredFont` below) rather than reverting
//    to a hardcoded value to "simplify" this.
// 3. The open menu is sized to the control's own rendered width (not independently recalculated),
//    which is why the menu can look "correctly sized" while the closed control still clips —
//    the menu has slack from its smaller OPTION_PADDING chrome reserve, the control doesn't.
//    Never treat "menu looks fine" as proof the control's text budget is also fine.
// 4. The wrapper `<div ref={containerRef}>` must stay `inline-block`/`block` only (never a plain
//    block with no display override) — otherwise its rendered width can silently diverge from
//    the control's own explicitly-styled width inside flex/grid ancestors (e.g. a flex-col
//    parent's default align-items: stretch), which breaks the ResizeObserver measurement this
//    whole model depends on.
// 5. `width` must never be allowed to exceed `max-width` on the control/menu styles below. When
//    content hits the cap, `controlWidth` (= capped content width + TEXT_MEASUREMENT_SAFETY_MARGIN)
//    can exceed a `max-width` that doesn't add the same margin — the control silently self-clamps
//    to max-width (CSS resolves width > max-width by using max-width), but the open menu's width
//    is fed from this control's own rendered box and set as `min-width` too, and min-width beats
//    max-width on a conflict — so the menu does NOT get the same clamp and ends up
//    TEXT_MEASUREMENT_SAFETY_MARGIN px wider than the closed control. This is exactly what caused
//    the closed control's right edge to sit in the wrong place while the open menu was correctly
//    aligned: two boxes meant to share one authoritative width, only one of which was ever actually
//    clamped. Keep every `max-width` here built from the exact same terms (+ the same margin) as
//    the `width` it bounds, or this reappears.
// 6. The open menu is rendered via `menuPortalTarget={document.body}` (see below), so it is NOT a
//    DOM descendant of this control — `fontSize: 'inherit'` on menuList/option/noOptionsMessage
//    therefore resolves against body's own ambient font-size, not this control's real one, and
//    silently renders larger/smaller than the closed control on any page where an ancestor (e.g.
//    a `text-sm` wrapper) changes the font-size away from the document default. Fixed by measuring
//    the real font-size too (`measuredFontSize`, read the same way as `measuredFont`) and setting
//    it explicitly on those three portaled styles, rather than relying on inheritance that the
//    portal boundary breaks. Don't revert those three back to 'inherit' to "simplify".
const MEASURE_FONT = '400 14px "IBM Plex Sans", sans-serif'
// Menu option row: 8px left + 12px right padding, plus a small safety margin.
const OPTION_PADDING = 24
// Non-text "chrome" inside a closed control that a menu option row doesn't have:
// dropdown indicator (36px) + indicatorsContainer padding (4px + 4px) + indicator
// separator (~1px) + valueContainer's own padding (8px + 8px). Without reserving
// this, a control sized the same as a menu option shows noticeably less text,
// because the indicator icon eats into the same row.
const CONTROL_CHROME = 61
// Extra reserve when isClearable, so the control doesn't need to resize at
// runtime once a value is picked and the clear button appears.
const CLEAR_INDICATOR_CHROME = 40
// react-select's control renders as box-sizing: border-box with a 1px border on each side, which
// CONTROL_CHROME doesn't otherwise account for (only the border color is overridden here).
const CONTROL_BORDER_CHROME = 2
// Pure headroom (never spent by the truncation budget) for residual px-level rounding between
// canvas and real DOM text layout — secondary to font-accuracy (see GEOMETRY NOTES above), which
// fixes the much larger proportional errors; this only mops up what's left after that.
const TEXT_MEASUREMENT_SAFETY_MARGIN = 6
const MIN_WIDTH = 100
const DEFAULT_MAX_CONTENT_WIDTH = 345
const VIEWPORT_SAFE_MARGIN = 16

// Reused across calls instead of created per-measurement.
let measureCanvas: HTMLCanvasElement | undefined

/** Measures real glyph width via canvas; returns null where canvas 2D isn't available (e.g. jsdom in tests). */
const measureTextWidth = (text: string, font: string): number | null => {
	if (typeof document === 'undefined') return null
	if (!measureCanvas) {
		measureCanvas = document.createElement('canvas')
	}
	const ctx = measureCanvas.getContext('2d')
	if (!ctx) return null
	ctx.font = font
	return ctx.measureText(text).width
}

/**
 * Estimates a label's raw text width in pixels (no padding/chrome), using real
 * font metrics with a character-count heuristic fallback for environments
 * without canvas support (e.g. jsdom in tests). `font` should be the control's
 * actual computed font (see `measuredFont` below) — the component deliberately
 * inherits font-size/family rather than fixing it, so a hardcoded measurement
 * font silently under/over-estimates width on any page with a different
 * ambient font-size, which is a much larger error than padding/chrome ever is.
 */
const measureLabelWidth = (text: string, font: string = MEASURE_FONT): number => {
	const measured = measureTextWidth(text, font)
	if (measured !== null) return measured
	const avgCharWidth = 7.5
	return text.length * avgCharWidth
}

/**
 * Calculates the control and menu widths based on the longest option label,
 * capped at maxContentWidth. The control gets `chromeReserve` extra px on top
 * of the text width (to make room for the indicator icon etc. within the same
 * capped box) while the menu only needs the option row's own padding.
 */
const calculateWidths = (
	options: readonly any[] | any[],
	maxContentWidth: number = DEFAULT_MAX_CONTENT_WIDTH,
	chromeReserve: number = CONTROL_CHROME,
	font: string = MEASURE_FONT
): { controlWidth: number; menuWidth: number } => {
	const labelWidths = (options || [])
		.map((opt: any) => (typeof opt?.label === 'string' ? measureLabelWidth(opt.label, font) : 0))
		.filter((width: number) => width > 0)
	const maxLabelWidth = labelWidths.length ? Math.max(...labelWidths) : 0

	const menuWidth = Math.max(MIN_WIDTH, Math.min(maxContentWidth, maxLabelWidth + OPTION_PADDING))
	const controlWidth = Math.max(MIN_WIDTH, Math.min(maxContentWidth + chromeReserve, maxLabelWidth + chromeReserve))
	return { controlWidth, menuWidth }
}

/** Clamps the open menu's width so it never renders past the right edge of the viewport. */
const clampMenuWidth = (desiredWidth: number, controlLeft: number, viewportWidth: number, safeMargin: number = VIEWPORT_SAFE_MARGIN): number => {
	const availableWidth = viewportWidth - controlLeft - safeMargin
	return Math.max(MIN_WIDTH, Math.min(desiredWidth, availableWidth))
}

// Sentinel values already used across the app's "Any"/"None" filter options (case-insensitive).
// When the current value is one of these, there's nothing meaningful to clear back to, so the
// clear button hides itself rather than offering to "clear" an already-cleared state.
const EMPTY_SENTINEL_VALUES = new Set(['', 'any', 'none'])

const isEmptySentinelOption = (option: any): boolean => {
	if (!option || Array.isArray(option)) return false
	return typeof option.value === 'string' && EMPTY_SENTINEL_VALUES.has(option.value.toLowerCase())
}

const TAIL_CHARS = 5
const ELLIPSIS = '...'

/**
 * Truncates a label to fit `availableWidth`, preserving the last `TAIL_CHARS`
 * characters (e.g. "Portfolio 1: Agriculture Fo...tions") rather than just
 * cutting the end — useful for labels whose distinguishing detail is at the
 * end (dates, IDs, file extensions). Falls back to the plain label when it
 * already fits or is too short for tail-preservation to be meaningful.
 */
const truncateMiddlePreservingTail = (label: string, availableWidth: number, font: string = MEASURE_FONT): string => {
	if (availableWidth <= 0 || label.length <= TAIL_CHARS) return label
	if (measureLabelWidth(label, font) <= availableWidth) return label

	const tail = label.slice(-TAIL_CHARS)
	const startBudget = availableWidth - measureLabelWidth(ELLIPSIS, font) - measureLabelWidth(tail, font)
	if (startBudget <= 0) return ELLIPSIS + tail

	let start = label.slice(0, -TAIL_CHARS)
	while (start.length > 0 && measureLabelWidth(start, font) > startBudget) {
		start = start.slice(0, -1)
	}
	return start.length > 0 ? start + ELLIPSIS + tail : ELLIPSIS + tail
}


/**
 * Composes a react-select style function: canonical default first, then any
 * caller-supplied override on top, then component-computed values last — so
 * neither the shared defaults nor a caller override can be silently dropped.
 */
const composeStyleFn = (
	defaultFn: ((base: any, state: any) => any) | undefined,
	callerFn: ((base: any, state: any) => any) | undefined,
	computed?: (base: any, state: any) => any
) => (base: any, state: any) => {
	let result = defaultFn ? defaultFn(base, state) : base
	if (typeof callerFn === 'function') result = callerFn(result, state)
	if (computed) result = computed(result, state)
	return result
}

const BORDER_RADIUS = '6px'
const borderColor = getColor('charcoal', '200') as string
const lightBorderColor = getColor('charcoal', '100') as string
const focusColor = getColor('algae', '500') as string
const textColor = getColor('charcoal', '800') as string
const placeholderColor = getColor('placeholder') as string
const disabledBackground = getColor('charcoal', '300') as string
const white = '#fff'
const selectedTextColor = getColor('charcoal', '900') as string
const optionHoverBackground = getColor('algae', '050') as string
const optionActiveBackground = getColor('algae', '100') as string
const controlIconColor = getColor('charcoal', '500') as string
const indicatorHoverBackground = getColor('algae', '050') as string

const defaultStyles = {
	control: (base: any, state: any) => ({
		...base,
		minHeight: FIELD_HEIGHT,
		height: FIELD_HEIGHT,
		cursor: state.isDisabled ? 'default' : 'pointer',
		borderRadius: BORDER_RADIUS,
		borderColor: state.isFocused ? focusColor : 'transparent',
		backgroundColor: state.isDisabled ? disabledBackground : white,
		fontSize: 'inherit',
		lineHeight: 'inherit',
		boxShadow: state.isFocused
			? `0 0 0 2px ${focusColor}`
			: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
		'&:hover': {
			borderColor: state.isFocused ? focusColor : 'transparent',
		},
	}),
	valueContainer: (base: any) => ({
		...base,
		height: FIELD_HEIGHT,
		padding: '0 8px',
		fontSize: 'inherit',
		lineHeight: 'inherit',
	}),
	input: (base: any) => ({
		...base,
		margin: 0,
		padding: 0,
		color: textColor,
		fontSize: 'inherit',
		lineHeight: 'inherit',
		outline: 'none',
		boxShadow: 'none',
		backgroundColor: 'transparent',
	}),
	singleValue: (base: any) => ({
		...base,
		color: textColor,
		fontSize: 'inherit',
		lineHeight: 'inherit',
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		maxWidth: '100%',
	}),
	placeholder: (base: any) => ({
		...base,
		color: placeholderColor,
		fontSize: 'inherit',
		lineHeight: 'inherit',
	}),
	indicatorsContainer: (base: any) => ({
		...base,
		padding: '2px 2px 2px 3px',
		height: FIELD_HEIGHT,
		display: 'flex',
		alignItems: 'center',
		position: 'relative',
		'&::before': {
			content: '""',
			position: 'absolute',
			left: 0,
			top: '8px',
			bottom: '8px',
			width: '1px',
			backgroundColor: lightBorderColor,
		},
	}),
	indicatorSeparator: (base: any) => ({
		...base,
		display: 'none',
	}),
	dropdownIndicator: (base: any) => ({
		...base,
		color: controlIconColor,
		padding: 0,
		margin: 0,
		width: '36px',
		height: '36px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: '6px',
		cursor: 'pointer',
		'&:hover': {
			color: controlIconColor,
			backgroundColor: indicatorHoverBackground,
		},
	}),
	clearIndicator: (base: any) => ({
		...base,
		color: controlIconColor,
		padding: 0,
		margin: '0 2px 0 0',
		width: '36px',
		height: '36px',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: '6px',
		cursor: 'pointer',
		'&:hover': {
			color: controlIconColor,
			backgroundColor: indicatorHoverBackground,
		},
	}),
	menu: (base: any, state: any) => ({
		...base,
		borderRadius: BORDER_RADIUS,
		boxShadow: '0 4px 12px 0 rgba(28, 35, 42, 0.15)',
		overflow: 'hidden',
		marginTop: '4px',
		/* z-index: 60 (DROPDOWN_MENU) — the open menu is always portaled to document.body (see
		   menuPortalTarget below), so it must outrank MODAL_OVERLAY (50): otherwise a dropdown used
		   as a form field inside a modal renders behind that modal's own overlay (greyed out,
		   unclickable) since the portal makes it a body-level sibling, not a descendant. */
		zIndex: zIndex.DROPDOWN_MENU,
		/* Width is applied by the component: same basis as the control, clamped to the viewport on open */
	}),
	menuPortal: (base: any) => ({
		...base,
		zIndex: zIndex.DROPDOWN_MENU,
	}),
	menuList: (base: any) => ({
		...base,
		padding: 0,
		fontSize: 'inherit',
		lineHeight: 'inherit',
	}),
	option: (base: any, state: any) => ({
		...base,
		cursor: 'pointer',
		fontSize: 'inherit',
		lineHeight: 'inherit',
		backgroundColor: state.isSelected
			? focusColor
			: state.isFocused
			? optionHoverBackground
			: white,
		color: state.isSelected ? selectedTextColor : textColor,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		padding: '8px 12px',
		'&:active': {
			backgroundColor: optionActiveBackground,
		},
	}),
	noOptionsMessage: (base: any) => ({
		...base,
		color: placeholderColor,
	}),
}

export type SearchableSelectWidthProps = {
	/**
	 * When true, the control stretches to fill its parent container's width
	 * instead of auto-sizing to the widest option label. Use for full-width
	 * form fields (e.g. modals, dedicated detail-page fields) where there's no
	 * reason to cap the width. Default false (auto-size to content, capped at
	 * `maxContentWidth`) — matches existing compact filter/inline-select usage.
	 */
	fillContainer?: boolean
	/**
	 * Caps the auto-sized width in px (default 345). Only applies when
	 * `fillContainer` is false. When `fillContainer` is true, this instead acts
	 * as an optional ceiling on top of the 100% width.
	 */
	maxContentWidth?: number
}

export type SearchableSelectCreatableProps<Option> = {
	/**
	 * When true, renders via `react-select/creatable` so typing a value with no
	 * matching option offers a "Create '<value>'" row (Sub-task I-extra2 §8.4 -
	 * "pick an existing org in this session, or type a new name to create one").
	 * No new dependency: `react-select/creatable` is a subpath of the already-
	 * installed `react-select` package. Default false (plain Select, unchanged
	 * behaviour for every other existing consumer).
	 */
	creatable?: boolean
	/** Required when `creatable` is true - called when the user picks the "create" row. */
	onCreateOption?: (inputValue: string) => void
	/** Customises the "create" row's label, e.g. `(value) => \`Create "${value}"\`` . */
	formatCreateLabel?: (inputValue: string) => React.ReactNode
	/** Override the default "non-empty and not an exact case-sensitive match" new-option rule. */
	isValidNewOption?: (inputValue: string, value: Option | readonly Option[] | null, options: readonly Option[]) => boolean
}

function SearchableSelect<
	Option extends SearchableSelectOption<unknown>,
	IsMulti extends boolean = false,
	Group extends GroupBase<Option> = GroupBase<Option>
>(props: ReactSelectProps<Option, IsMulti, Group> & SearchableSelectWidthProps & SearchableSelectCreatableProps<Option>) {
	const {
		fillContainer = false,
		maxContentWidth: maxContentWidthProp,
		creatable = false,
		onCreateOption,
		formatCreateLabel,
		isValidNewOption,
		...selectProps
	} = props
	const maxContentWidth = maxContentWidthProp ?? DEFAULT_MAX_CONTENT_WIDTH
	const options = selectProps.options || []
	// A clear "x" should be available anywhere a selection can represent "no choice" (e.g. an
	// "Any" option) — the canonical component defaults it on; callers with a genuinely required
	// field opt back out with isClearable={false} rather than each place opting in individually.
	const isClearable = selectProps.isClearable ?? true
	// Reserve extra room for the clear button up front so the control doesn't resize at runtime
	// once a value is picked (matches the existing "size for the whole list up front" behaviour).
	const chromeReserve = CONTROL_CHROME + CONTROL_BORDER_CHROME + (isClearable ? CLEAR_INDICATOR_CHROME : 0)

	const containerRef = useRef<HTMLDivElement>(null)
	const [measuredWidth, setMeasuredWidth] = useState<number | null>(null)
	// The component deliberately inherits font-size/family (see defaultStyles) rather than fixing
	// it, so different pages can render this control at very different real sizes (e.g. the plain
	// document default vs. a text-sm ancestor) — measuring against a hardcoded font is wrong by a
	// proportional (not just a few px) amount. Read the real computed font from our own wrapper
	// once mounted (it doesn't override font itself, so it reflects whatever a descendant control
	// will actually render at) and fall back to the MEASURE_FONT constant only pre-mount.
	const [measuredFont, setMeasuredFont] = useState<string | null>(null)
	const font = measuredFont ?? MEASURE_FONT
	// The portaled menu (see `menuPortalTarget` below) renders as a direct child of <body>, outside
	// this control's own DOM subtree — `fontSize: 'inherit'` on its styles (defaultStyles.menuList/
	// option) therefore resolves against body's ambient font-size, not this control's real one (e.g.
	// a page wrapping the control in a `text-sm` ancestor). Track the real computed font-size here so
	// the menu styles below can set it explicitly rather than relying on inheritance that the portal
	// boundary breaks.
	const [measuredFontSize, setMeasuredFontSize] = useState<string | null>(null)
	const { controlWidth: contentControlWidth, menuWidth: contentMenuWidth } = calculateWidths(options, maxContentWidth, chromeReserve, font)
	// The safety margin only ever widens the physical control box; it's never counted against the
	// truncation budget below, so it acts as pure slack rather than extra allowance for longer text.
	const controlWidth = contentControlWidth + TEXT_MEASUREMENT_SAFETY_MARGIN
	const [menuWidth, setMenuWidth] = useState(contentMenuWidth)
	const [isMenuOpen, setIsMenuOpen] = useState(false)

	// The menu must always match the control's own rendered width (never independently
	// narrower/wider) — so track it continuously via ResizeObserver, not just on mount/window
	// resize. A plain block div's width is otherwise dictated by whatever the parent layout hands
	// it (e.g. a flex-col ancestor's default `align-items: stretch`), which can silently diverge
	// from the actual control's own explicitly-styled width — ResizeObserver reacts to that box
	// actually changing size for any reason (sibling reflow, font load, etc.), not just a window
	// resize, which is what caused the mismatch to appear/disappear seemingly at random.
	useEffect(() => {
		const node = containerRef.current
		if (!node) return undefined
		const measure = () => {
			const rect = node.getBoundingClientRect()
			// A 0 width means "not laid out yet" (or a test/non-layout environment) rather than a
			// genuine measurement — ignore it so the content-based fallback stays in effect.
			if (rect.width > 0) setMeasuredWidth(rect.width)
		}
		measure()
		if (typeof ResizeObserver === 'undefined') {
			// jsdom (tests) has no ResizeObserver — fall back to the window resize listener so the
			// content-based estimate keeps driving assertions deterministically.
			window.addEventListener('resize', measure)
			return () => window.removeEventListener('resize', measure)
		}
		const observer = new ResizeObserver(measure)
		observer.observe(node)
		return () => observer.disconnect()
	}, [])

	useEffect(() => {
		const node = containerRef.current
		if (!node || typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') return undefined
		const readFont = () => {
			const computed = window.getComputedStyle(node)
			const resolved = `${computed.fontWeight} ${computed.fontSize} ${computed.fontFamily}`.trim()
			// A blank result means computed styles aren't meaningfully resolvable (e.g. jsdom without
			// real layout/font loading) — keep the MEASURE_FONT fallback rather than store junk.
			if (resolved.trim() !== '') setMeasuredFont(resolved)
			if (computed.fontSize) setMeasuredFontSize(computed.fontSize)
		}
		readFont()
		// Web fonts (IBM Plex Sans) load asynchronously — re-read once real glyph metrics are ready so
		// an initial fallback-font measurement doesn't stick around and under/over-size the control.
		if (typeof document !== 'undefined' && document.fonts?.ready) {
			document.fonts.ready.then(readFont).catch(() => undefined)
		}
		return undefined
	}, [])

	// Before the control has been measured (e.g. first paint), fall back to the content estimate.
	const renderedControlWidth = measuredWidth ?? controlWidth

	useEffect(() => {
		setMenuWidth(renderedControlWidth)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [renderedControlWidth])

	// Re-clamp against the viewport while the menu is open and the window resizes.
	useEffect(() => {
		if (!isMenuOpen) return undefined
		const handleResize = () => {
			const rect = containerRef.current?.getBoundingClientRect()
			if (!rect) return
			setMenuWidth(clampMenuWidth(renderedControlWidth, rect.left, window.innerWidth))
		}
		window.addEventListener('resize', handleResize)
		return () => window.removeEventListener('resize', handleResize)
	}, [isMenuOpen, renderedControlWidth])

	const handleMenuOpen = () => {
		setIsMenuOpen(true)
		const rect = containerRef.current?.getBoundingClientRect()
		if (rect) setMenuWidth(clampMenuWidth(renderedControlWidth, rect.left, window.innerWidth))
		selectProps.onMenuOpen?.()
	}

	const handleMenuClose = () => {
		setIsMenuOpen(false)
		selectProps.onMenuClose?.()
	}

	// Truncation budgets derived from the same rendered width the control/menu actually use,
	// so the cut point always matches what's visually available (see chrome reserve above).
	// The safety margin is subtracted back out here so it stays pure headroom, not extra text allowance.
	const valueTextBudget = Math.max(0, renderedControlWidth - chromeReserve - TEXT_MEASUREMENT_SAFETY_MARGIN)
	const optionTextBudget = Math.max(0, renderedControlWidth - OPTION_PADDING)

	const defaultFormatOptionLabel = (option: any, meta: { context: 'menu' | 'value' }) => {
		const label = option?.label
		if (typeof label !== 'string') return label
		const budget = meta.context === 'value' ? valueTextBudget : optionTextBudget
		return truncateMiddlePreservingTail(label, budget, font)
	}

	// Compose canonical defaults + any caller override + the computed width, so neither is silently dropped.
	const composedStyles = {
		...selectProps.styles,
		container: composeStyleFn(
			(defaultStyles as any).container,
			selectProps.styles?.container as any,
			(base: any) => ({ ...base, width: fillContainer ? '100%' : `${controlWidth}px` })
		),
		control: composeStyleFn(
			defaultStyles.control,
			selectProps.styles?.control as any,
			(base: any) => ({
				...base,
				width: fillContainer ? '100%' : `${controlWidth}px`,
				// Must include the same safety margin as `controlWidth` itself, or `width` can exceed
				// `max-width` whenever content hits the cap (maxLabelWidth + chromeReserve ===
				// maxContentWidth + chromeReserve, then +6 pushes width past this max-width) — a
				// self-contradictory box the control silently resolves by clamping to max-width, while
				// the open menu (built from this control's own *measured* — i.e. already-clamped-or-not
				// — rendered width, see `measuredWidth` below) does NOT hit the same clamp, so the two
				// silently diverge by exactly the safety-margin px. This was the exact cause of the
				// "closed control right edge misaligned, opens correctly" bug: two boxes fed from what
				// should be one authoritative width, but only one of them was ever actually clamped.
				maxWidth: fillContainer
					? (maxContentWidthProp ? `${maxContentWidthProp}px` : 'none')
					: `${maxContentWidth + chromeReserve + TEXT_MEASUREMENT_SAFETY_MARGIN}px`,
			})
		),
		menu: composeStyleFn(
			defaultStyles.menu,
			selectProps.styles?.menu as any,
			(base: any) => ({
				...base,
				width: `${menuWidth}px`,
				minWidth: `${menuWidth}px`,
				// See the matching comment on `control.maxWidth` above — this must stay in lockstep with
				// it (same +TEXT_MEASUREMENT_SAFETY_MARGIN) so the menu is never a different authoritative
				// width than the closed control it belongs to.
				maxWidth: fillContainer ? 'none' : `${maxContentWidth + chromeReserve + TEXT_MEASUREMENT_SAFETY_MARGIN}px`,
			})
		),
		// menuList/option/noOptionsMessage render inside the portaled menu (outside this control's own
		// DOM subtree), so their default fontSize: 'inherit' can't reach this control's real ambient
		// size — set it explicitly from the real measured value so the open menu's text always matches
		// the closed control, regardless of document.body's own ambient font-size.
		menuList: composeStyleFn(
			defaultStyles.menuList,
			selectProps.styles?.menuList as any,
			(base: any) => (measuredFontSize ? { ...base, fontSize: measuredFontSize } : base)
		),
		option: composeStyleFn(
			defaultStyles.option,
			selectProps.styles?.option as any,
			(base: any) => (measuredFontSize ? { ...base, fontSize: measuredFontSize } : base)
		),
		noOptionsMessage: composeStyleFn(
			defaultStyles.noOptionsMessage,
			selectProps.styles?.noOptionsMessage as any,
			(base: any) => (measuredFontSize ? { ...base, fontSize: measuredFontSize } : base)
		),
	}

	// CreatableSelect wraps Select and accepts every prop it does, plus onCreateOption/
	// formatCreateLabel/isValidNewOption - sharing this one JSX block (rather than two near-
	// duplicate ones) keeps every width/style/measurement behaviour above in lockstep between
	// the plain and creatable variants.
	const SelectComponent = (creatable ? CreatableSelect : Select) as typeof Select

	return (
		// inline-block (rather than a plain block div) so this wrapper always shrink-wraps to the
		// actual control's own width instead of being stretched by an ancestor flex/grid layout
		// (e.g. a flex-col parent's default align-items: stretch) — keeps our own width measurement
		// honest and stops the control from silently growing wider than intended when there's room.
		<div ref={containerRef} style={{ display: fillContainer ? 'block' : 'inline-block' }}>
			<SelectComponent<Option, IsMulti, Group>
				placeholder='Search or select…'
				{...selectProps}
				{...(creatable ? { onCreateOption, formatCreateLabel, isValidNewOption } : {})}
				isClearable={isClearable}
				// Creatable selects are easy to mistake for "pick one of these existing options only" -
				// hiding the current value while the menu is open (so the placeholder shows instead)
				// hints that typing starts a brand-new entry rather than filtering the existing one.
				// Restores the value once the menu closes if nothing new was chosen.
				controlShouldRenderValue={selectProps.controlShouldRenderValue ?? (creatable ? !isMenuOpen : true)}
				formatOptionLabel={selectProps.formatOptionLabel ?? (defaultFormatOptionLabel as any)}
				menuPortalTarget={selectProps.menuPortalTarget ?? (typeof document !== 'undefined' ? document.body : undefined)}
				menuPlacement={selectProps.menuPlacement ?? 'auto'}
				menuPosition={selectProps.menuPosition ?? 'fixed'}
				classNamePrefix='searchable-select'
				onMenuOpen={handleMenuOpen}
				onMenuClose={handleMenuClose}
				components={{
					ClearIndicator: (indicatorProps) => {
						// Hide the clear button once the value is already "Any"/"None" — there's nothing left
						// to clear back to, so showing it invites clearing an already-empty selection.
						if (isEmptySentinelOption(indicatorProps.selectProps.value)) return null
						return <components.ClearIndicator {...indicatorProps} innerProps={{ ...indicatorProps.innerProps, 'aria-label': 'Clear selection' }} />
					},
					DropdownIndicator: (indicatorProps) => (
						<components.DropdownIndicator {...indicatorProps} innerProps={{ ...indicatorProps.innerProps, 'aria-label': 'Open menu' }} />
					),
					...selectProps.components,
				}}
				styles={{
					...defaultStyles,
					...composedStyles,
				}}
			/>
		</div>
	)
}


export { calculateWidths, clampMenuWidth, defaultStyles }
export default SearchableSelect
